/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { HiArrowUturnDown, HiArrowPath } from "react-icons/hi2"; // Npm: react-icons library.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { Flex, Text, Button, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import VendorReportDropDownOptions from "components/VendorReportDropDownOptions";

/*
 * GRAPHS
 */
import VendorReadQuery from "./__query__/index.vendor.read.query";

/*
 * OBJECTS
 */
const Index = () => {
  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );

  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : null;

  const _QueryVendorRead = useQuery(VendorReadQuery, {
    variables: {
      filter: _appliedFilter,
      skip: paginationModel?.pageSize * paginationModel?.page,
      take: paginationModel?.pageSize,
      smsStartDate: dateRange?.selection.startDate,
      smsEndDate: dateRange?.selection.endDate,
    },
    fetchPolicy: "cache-and-network",
  });

  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 130,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        hide: true,
      },
      {
        field: "displayName",
        headerName: "Vendor Name",
        description: "Name of the vendor.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 250,
      },
      {
        field: "totalCount",
        headerName: "Total Count",
        description: "Total SMS count.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsDelivered",
        headerName: "SMS Delivered",
        description: "Total delivered SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsPending",
        headerName: "SMS Pending",
        description: "Total pending SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsFailed",
        headerName: "SMS Failed",
        description: "Total failed SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsUndeliverable",
        headerName: "SMS Undeliverable",
        description: "Total undeliverable SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsRejected",
        headerName: "SMS Rejected",
        description: "Total rejected SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "dlrPercent",
        headerName: "DLR %",
        description: "Delivery Rate Percentage.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "action",
        headerName: "Actions",
        description: "Actions for vendor.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <VendorReportDropDownOptions
            vendorId={row?.id}
            currency={row?.currency}
          />
        ),
      },
    ],
    [],
  );

  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryVendorRead?.data?.VendorRead?.map((r, index) =>
          "READ_SUCCESSFUL" === r.status
            ? {
                id: r?.id || String.random(9),
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                displayName: r?.displayName ?? "-",
                totalCount:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsCount ?? "-",
                smsDelivered:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsDelivered ?? "-",
                smsPending:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsAccepted ?? "-",
                smsFailed:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsFailed ?? "-",
                smsUndeliverable:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsUndelivered ?? "-",
                smsRejected:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsRejected ?? "-",
                dlrPercent: (() => {
                  const totalCount =
                    r?._smsCountForGivenVendor?.[r?.id]?.smsCount;
                  const smsDelivered =
                    r?._smsCountForGivenVendor?.[r?.id]?.smsDelivered;
                  if (totalCount && smsDelivered && totalCount > 0) {
                    return ((smsDelivered / totalCount) * 100).toFixed(2) + "%";
                  }
                  return "-";
                })(),
                currency: r?.Billing?.currency,
              }
            : null,
        ),
      ),
    [_QueryVendorRead?.data?.VendorRead, paginationModel.page],
  );

  // Return component.
  return (
    <>
      <Flex className="reportsVendor base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="80vh"
          maxH="80vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" alignItems="center" gap="22px">
            <Flex flex={0.5} gap="12px" flexDir="column">
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date."}
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex
              flex={0.5}
              justifyContent="flex-end"
              gap="12px"
              alignItems="center"
            >
              <Button
                borderRadius="12px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                onClick={() => _QueryVendorRead.refetch()}
                isDisabled={_QueryVendorRead?.loading}
                leftIcon={<HiArrowPath />}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={_QueryVendorRead?.data?.VendorRead?.map(
                  (item, __index) =>
                    "READ_SUCCESSFUL" === item.status
                      ? {
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          "Vendor Name": item?.displayName ?? "-",
                          "Total Count":
                            item?._smsCountForGivenVendor?.[item?.id]
                              ?.smsCount ?? "-",
                          "Sms Delivered":
                            item?._smsCountForGivenVendor?.[item?.id]
                              ?.smsDelivered ?? "-",
                          "Sms Failed":
                            item?._smsCountForGivenVendor?.[item?.id]
                              ?.smsFailed ?? "-",
                          "Sms Pending":
                            item?._smsCountForGivenVendor?.[item?.id]
                              ?.smsAccepted ?? "-",
                          "Sms Undeliverable":
                            item?._smsCountForGivenVendor?.[item?.id]
                              ?.smsUndelivered ?? "-",
                          "Sms Rejected":
                            item?._smsCountForGivenVendor?.[item?.id]
                              ?.smsRejected ?? "-",
                          "DLR %": (() => {
                            const totalCount =
                              item?._smsCountForGivenVendor?.[item?.id]
                                ?.smsCount;
                            const smsDelivered =
                              item?._smsCountForGivenVendor?.[item?.id]
                                ?.smsDelivered;
                            if (totalCount && smsDelivered && totalCount > 0) {
                              return (
                                ((smsDelivered / totalCount) * 100).toFixed(2) +
                                "%"
                              );
                            }
                            return "-";
                          })(),
                        }
                      : void 0,
                )}
                headersData={[
                  "S.No.",
                  "Vendor Name",
                  "Total Count",
                  "Sms Delivered",
                  "Sms Failed",
                  "Sms Pending",
                  "Sms Undeliverable",
                  "Sms Rejected",
                  "DLR %",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={
              _.pluck(_QueryVendorRead?.data?.VendorRead, "_totalCount")?.[0] ||
              0
            }
            paginationMode="server"
            filterMode="server"
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={_setFilterDebounced}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryVendorRead?.loading}
          />
        </Flex>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORTS
 */
export default Index;
